import { getTracker, DOT_CORE_MODULE_CONFIGURE, DotCoreModule, DOT_CORE_FEATURE_CONFIGURE } from '@ng-dot/core';
import * as i0 from '@angular/core';
import { InjectionToken, isDevMode, Injectable, inject, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { API_LOG } from '@js-dot/api';
import { Identifier, toKebabCase } from '@js-dot/core';
import { DOT_TRANSLATE_FEATURE_CONFIGURE } from '@ng-dot/text';
import { DotBannerService } from '@ng-dot/ui/banner';
import { catchError } from 'rxjs/operators';
import * as i1 from '@ng-dot/ui/toast';

/**
 * 초기화 실패 안내
 *
 * @param message 개발자 작성 문구
 * @param reason 발생 오류
 */
function failedInitialize(message, reason) {
  reason && console.error(reason);
  import('./im-ng-core-failed-initialize-chunk-DsZM_oCW.mjs').then(({
    render
  }) => {
    render(message, reason);
  });
}

/**
 * trackBy 인자 함수
 * `seq` 사용
 *
 * @example
 * ```ts
 * interface Foo {
 * 	seq: number
 * }
 * @Component({
 * 	template: `
 * 		<ng-container *ngFor="let item of items; trackBy: usingSeq" />
 * 	`
 * })
 * class FooComponent {
 * 	usingSeq: SeqOf<Foo> = TRACKING_SEQ
 * }
 * ```
 */
const TRACKING_SEQ = getTracker('seq');
/**
 * trackBy 인자 함수
 * `id` 사용
 *
 * @example
 * ```ts
 * interface Foo {
 * 	id: number
 * }
 * @Component({
 * 	template: `
 * 		<ng-container *ngFor="let item of items; trackBy: usingId" />
 * 	`
 * })
 * class FooComponent {
 * 	usingId: IdOf<Foo> = TRACKING_ID
 * }
 * ```
 */
const TRACKING_ID = getTracker('id');
/**
 * trackBy 인자 함수
 * `seq`  | `id` | `path` 사용
 *
 * @example
 * ```ts
 * interface Foo {
 * 	id: number
 * }
 * @Component({
 * 	template: `
 * 		<ng-container *ngFor="let item of items; trackBy: usingIdLike" />
 * 	`
 * })
 * class FooComponent {
 * 	usingIdLike: IdLikeOf<Foo> = TRACKING_ID_LIKE
 * }
 * ```
 */
const TRACKING_ID_LIKE = (index, item) => item['seq'] || item['id'] || item['key'] || index;
const CORE_FEATURE_CONFIGURE = new InjectionToken('#im/core/feature');

/**
 * HTTP 요청에 대한 인증 오류 처리
 * @description
 * - 401 오류에 대한 인증 해제
 * - 기타 오류에 대한 정보 출력
 *   - 주소에 `flag=` 포함 시 무시
 */
class HttpInterceptor {
  constructor(_toast) {
    this._toast = _toast;
    this.development = isDevMode();
  }
  intercept(request, next) {
    const {
      development
    } = this;
    return next.handle(request).pipe(catchError((event, caught) => {
      if (development) {
        const {
          error,
          url
        } = event;
        if (!url || url.includes('_http_=polyfill')) {
          throw error;
        }
        let {
          message
        } = event;
        this._toast.critical({
          id: url,
          body: message,
          icon: 'icon24:code'
        });
      }
      throw event;
    }));
  }
  static {
    this.ɵfac = function HttpInterceptor_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || HttpInterceptor)(i0.ɵɵinject(i1.DotToastService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: HttpInterceptor,
      factory: HttpInterceptor.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpInterceptor, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.DotToastService
  }], null);
})();

/**
 * 비 결합(decoupling)성 모듈 관리 모듈
 */
class CoreModule {
  /**
   * 핵심 모듈 활성화
   * @example
   * ```ts
   * @NgModule({
   *     imports: [
   *         CoreModule.forRoot()
   *     ]
   * })
   * export class AppModule {
   * }
   * ```
   * @param configure
   */
  static forRoot(configure) {
    const providers = [{
      provide: DOT_CORE_MODULE_CONFIGURE,
      useValue: configure
    }];
    if (!configure?.disableToastForHttpError) {
      providers[providers.length] = {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpInterceptor,
        multi: true
      };
    }
    return {
      ngModule: CoreModule,
      providers
    };
  }
  /**
   * 동적 모듈 활성 및 통합 설정
   *
   * @description
   * 통합 대상:
   *
   * - DotCoreModule.forFeature()
   * - DotTranslate.forFeature()
   *
   * @see import('@ng-dot/core').DotCoreModule
   * @see import('@ng-dot/text').DotTranslateModule
   * @param namespace_or_configure
   */
  static forModule(namespace_or_configure) {
    return {
      ngModule: CoreModule,
      providers: getMergedProviders(namespace_or_configure)
    };
  }
  constructor(injector) {
    if (isDevMode() && !CoreModule.logged) {
      CoreModule.logged = true;
      const {
        location
      } = injector.get(DOCUMENT);
      if (location.hostname === 'localhost' && +location.port > 3999 && +location.port < 5000) {
        API_LOG.enable();
      }
      const _banner = inject(DotBannerService);
      Promise.resolve()
      // .then(() => import('@js-dot/dev/tip'))
      // .then(() => import('@ng-dot/dev/tip'))
      .then(() => import('@js-dot/dev/change-log')).then(() => import('@ng-dot/dev/change-log')).then(() => import('@im-js/dev/change-log')).then(() => {
        import('@im-ng/dev/change-log').then(({
          imNgChangeLog
        }) => {
          imNgChangeLog.then(({
            logs
          }) => {
            logs.map(({
              dateString,
              content
            }) => {
              _banner.open({
                id: '[@im-ng]' + dateString,
                content: `${dateString}\n${content}`
              });
            });
          });
        });
      });
    }
  }
  static {
    this.ɵfac = function CoreModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CoreModule)(i0.ɵɵinject(i0.Injector));
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: CoreModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [DotCoreModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CoreModule, [{
    type: NgModule,
    args: [{
      imports: [DotCoreModule]
    }]
  }], () => [{
    type: i0.Injector
  }], null);
})();
/**
 * 병합된 모듈 설정으로 부터 공급자 생성
 * @param namespace_or_configure
 */
function getMergedProviders(namespace_or_configure) {
  const providers = [];
  let configure = typeof namespace_or_configure === 'string' || namespace_or_configure instanceof Identifier ? {
    namespace: namespace_or_configure
  } : namespace_or_configure;
  if (configure.namespace instanceof Identifier) {
    if (!('translate' in configure) || configure.translate !== false && !configure.translate?.wordId) {
      if (!configure.translate) {
        configure.translate = {};
      }
      configure.translate.wordId = toKebabCase(configure.namespace.description.slice(1).toLowerCase());
    }
  }
  if ('translate' in configure) {
    providers[providers.length] = {
      provide: DOT_TRANSLATE_FEATURE_CONFIGURE,
      useValue: configure.translate,
      multi: true
    };
    delete configure['translate'];
  }
  providers[providers.length] = {
    provide: DOT_CORE_FEATURE_CONFIGURE,
    useValue: configure,
    multi: true
  };
  return providers;
}

/**
 * Generated bundle index. Do not edit.
 */

export { CORE_FEATURE_CONFIGURE, CoreModule, TRACKING_ID, TRACKING_ID_LIKE, TRACKING_SEQ, failedInitialize };
